import { createClient } from '@/lib/supabase/browser';
import { User } from '@supabase/supabase-js';
import type { Role } from '@/types/user';
import { normalizeUserRecord, normalizeUserProfile, safeString } from '@/lib/row-utils';

export interface UserRecord {
  id: string;
  email: string;
  username?: string;
  full_name?: string;
  avatar_url?: string;
  role: 'talent' | 'scout' | 'admin';
  created_at: string;
  updated_at: string;
}

export interface UserProfile {
  id: string;
  email: string;
  full_name?: string;
  preferred_name?: string;
  avatar_url?: string;
  default_avatar?: string;
  username?: string;
  bio?: string;
  location?: string;
  experience_level?: string;
  specialties?: string[];
  tags?: string[];
  account_type?: 'talent' | 'scout';
  onboarding_completed?: boolean;
  onboarding_step?: number | null;
  public_profile?: boolean;
  account_approval_status?: 'pending' | 'approved' | 'rejected';
  professional_title?: string;
  company_name?: string;
  company_website?: string;
  years_in_industry?: string;
  talent_types_hired?: string[];
  sample_projects?: string;
  privacy_setting?: 'public' | 'talent_only' | 'private';
  created_at: string;
  updated_at: string;
}

export function coerceRole(input: unknown): Role {
  if (input === 'scout' || input === 'admin') return input;
  return 'talent';
}

export function mergeProfileRole(p: Partial<UserProfile>): UserProfile {
  return {
    id: p.id!,
    email: p.email!,
    username: p.username,
    full_name: p.full_name,
    preferred_name: p.preferred_name,
    avatar_url: p.avatar_url,
    default_avatar: p.default_avatar,
    bio: p.bio,
    location: p.location,
    experience_level: p.experience_level,
    specialties: p.specialties,
    tags: p.tags,
    account_type: p.account_type as 'talent' | 'scout',
    onboarding_completed: p.onboarding_completed,
    onboarding_step: p.onboarding_step,
    public_profile: p.public_profile,
    account_approval_status: p.account_approval_status,
    professional_title: p.professional_title,
    company_name: p.company_name,
    company_website: p.company_website,
    years_in_industry: p.years_in_industry,
    talent_types_hired: p.talent_types_hired,
    sample_projects: p.sample_projects,
    privacy_setting: p.privacy_setting,
    created_at: p.created_at ?? new Date().toISOString(),
    updated_at: p.updated_at ?? new Date().toISOString(),
  };
}

export function getUserRoleFlags(role: Role) {
  return {
    isTalent: role === 'talent',
    isScout: role === 'scout',
    isAdmin: role === 'admin',
  };
}

/**
 * Ensure user metadata is inserted or updated in the users table
 * This function is called after Google Auth login to ensure the users table is never empty
 */
export async function ensureUserInDatabase() {
  try {
    const supabase = createClient();
    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError || !user) {
      console.error("❌ User not authenticated", authError);
      return { success: false, error: authError };
    }

    console.log('🔄 Ensuring user in database:', user.email);

    // Check if user already exists
    const { data: existingUser, error: fetchError } = await supabase
      .from("profiles")
      .select("id")
      .eq("id", user.id)
      .maybeSingle();

    if (fetchError && fetchError.code !== "PGRST116") {
      console.error("❌ Error checking existing user:", fetchError);
      return { success: false, error: fetchError };
    }

    if (!existingUser) {
      // Insert new user record
      const { error: insertError } = await supabase.from("profiles").insert({
        id: user.id,
        email: user.email,
        username: user.user_metadata?.username || user.user_metadata?.user_name || "",
        display_name: user.user_metadata?.display_name || user.user_metadata?.full_name || "",
        full_name: user.user_metadata?.full_name || "",
        avatar_url: user.user_metadata?.avatar_url || "",
        onboarding_completed: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });

      if (insertError) {
        console.error("❌ Error inserting new user:", insertError);
        return { success: false, error: insertError };
      } else {
        console.log("✅ User inserted successfully");
        return { success: true, error: null };
      }
    } else {
      console.log("✅ User already exists");
      return { success: true, error: null };
    }
  } catch (error) {
    console.error("❌ Error in ensureUserInDatabase:", error);
    return { success: false, error };
  }
}

/**
 * Sync user profile after signup or login
 * This function handles both new users and existing users
 */
export async function syncUserProfile(user: User, role: 'talent' | 'scout' = 'talent') {
  if (!user) {
    console.error('❌ No user provided to syncUserProfile');
    return { data: null, error: new Error('No user provided') };
  }

  try {
    const supabase = createClient();
    const { id, email, user_metadata } = user;
    
    console.log('🔄 Syncing user profile for:', {
      id,
      email,
      full_name: user_metadata?.full_name,
      avatar_url: user_metadata?.avatar_url,
      role
    });

    // Check if user exists in profiles table
    const { data: existingUser, error: userCheckError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (userCheckError) {
      console.error('❌ Error checking existing user:', userCheckError);
      return { data: null, error: userCheckError };
    }

    let userRecord: UserRecord;

    if (existingUser) {
      console.log('✅ User already exists in profiles table:', existingUser);
      userRecord = normalizeUserRecord(existingUser) || existingUser as any;
      
      // Update user record if needed (e.g., email changes)
      if (existingUser.email !== email) {
        const updateData: Partial<UserRecord> = {
          email: email || safeString(existingUser, 'email'),
          updated_at: new Date().toISOString(),
        };

        // Update other fields if we have new metadata
        if (user_metadata?.full_name && !existingUser.full_name) {
          updateData.full_name = user_metadata.full_name;
        }
        if (user_metadata?.avatar_url && !existingUser.avatar_url) {
          updateData.avatar_url = user_metadata.avatar_url;
        }
        if (user_metadata?.user_name && !existingUser.username) {
          updateData.username = user_metadata.user_name;
        }

        const { data: updatedUser, error: updateError } = await supabase
          .from('profiles')
          .update(updateData)
          .eq('id', id)
          .select()
          .maybeSingle();

        if (updateError) {
          console.error('❌ Error updating user record:', updateError);
        } else if (updatedUser) {
          userRecord = normalizeUserRecord(updatedUser) || updatedUser as any;
          console.log('✅ User record updated:', updatedUser);
        }
      }
    } else {
      // Create new user record
      console.log('🔄 Creating new user record...');
      const full_name = user_metadata?.full_name || user_metadata?.name || email?.split('@')[0] || 'User';
      const avatar_url = user_metadata?.avatar_url || null;
      const username = user_metadata?.user_name || email?.split('@')[0] || null;

      const userInsertData = {
        user_id: id,
        email: email || '',
        username,
        full_name,
        avatar_url,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const { data: newUser, error: createUserError } = await supabase
        .from('profiles')
        .upsert(userInsertData, { onConflict: 'user_id', ignoreDuplicates: false })
        .select()
        .maybeSingle();

      if (createUserError) {
        console.error('❌ Failed to create user record:', createUserError);
        return { data: null, error: createUserError };
      }

      if (!newUser) {
        console.error('❌ No user record returned after creation');
        return { data: null, error: 'Failed to create user record' };
      }

      console.log('✅ User record created successfully:', newUser);
      userRecord = normalizeUserRecord(newUser) || newUser as any;
    }

    // Now handle the profiles table
    const { data: existingProfile, error: profileCheckError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (profileCheckError) {
      console.error('❌ Error checking existing profile:', profileCheckError);
      return { data: null, error: profileCheckError };
    }

    let profileRecord: UserProfile;

    if (existingProfile) {
      console.log('✅ Profile already exists:', existingProfile);
      profileRecord = normalizeUserProfile(existingProfile) || existingProfile as any;
      
      // Update profile with any new metadata from auth
      const updateData: Partial<UserProfile> = {
        updated_at: new Date().toISOString(),
      };

      // Only update if we have new data
      if (user_metadata?.full_name && !existingProfile.full_name) {
        updateData.full_name = user_metadata.full_name;
      }
      if (user_metadata?.avatar_url && !existingProfile.avatar_url) {
        updateData.avatar_url = user_metadata.avatar_url;
      }
      if (email && existingProfile.email !== email) {
        updateData.email = email;
      }

              if (Object.keys(updateData).length > 1) { // More than just updated_at
                  const { data: updatedProfile, error: updateError } = await supabase
          .from('profiles')
          .update(updateData)
          .eq('id', id)
          .select()
          .maybeSingle();

        if (updateError) {
          console.error('❌ Error updating profile:', updateError);
        } else {
          profileRecord = normalizeUserProfile(updatedProfile) || updatedProfile as any;
          console.log('✅ Profile updated:', updatedProfile);
        }
      }
    } else {
      // Create new profile
      console.log('🔄 Creating new profile...');
      const full_name = user_metadata?.full_name || user_metadata?.name || email?.split('@')[0] || 'User';
      const avatar_url = user_metadata?.avatar_url || null;
      const username = user_metadata?.user_name || email?.split('@')[0] || null;

      const profileInsertData = {
        id: id,
        email: email || '',
        full_name,
        preferred_name: user_metadata?.preferred_name || null,
        avatar_url,
        username,
        // The legacy `role` arg here ('talent'|'scout') maps to account_type
        // now. The canonical permission column profiles.role accepts only
        // ('user','moderator','admin','super_admin') — anything else trips
        // the CHECK constraint, so default to 'user'.
        account_type: role as 'talent' | 'scout',
        role: 'user',
        onboarding_completed: false,
        public_profile: true,
        bio: '',
        location: '',
        experience_level: '',
        specialties: [],
        tags: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const { data: newProfile, error: createProfileError } = await supabase
        .from('profiles')
        .upsert(profileInsertData, { onConflict: 'id', ignoreDuplicates: false })
        .select()
        .maybeSingle();

      if (createProfileError) {
        console.error('❌ Failed to create profile:', createProfileError);
        return { data: null, error: createProfileError };
      }

      console.log('✅ Profile created successfully:', newProfile);
      profileRecord = normalizeUserProfile(newProfile) || newProfile as any;
    }

    console.log('✅ User and profile sync completed successfully');
    console.log('✅ Final user record:', userRecord);
    console.log('✅ Final profile record:', profileRecord);

    return {
      data: {
        user: userRecord,
        profile: profileRecord
      },
      error: null
    };

  } catch (error) {
    console.error('❌ Error in syncUserProfile:', error);
    return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
  }
}

/**
 * Create or fetch user after Google OAuth sign-in
 * Creates entries in both users and profiles tables
 * @deprecated Use syncUserProfile instead
 */
export async function createOrFetchUser(user: User) {
  // Get role from user metadata, default to 'talent' if not specified
  const role = user.user_metadata?.role || 'talent';
  return syncUserProfile(user, role);
}

/**
 * Get user record by ID
 */
export async function getUserRecord(userId: string) {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error('❌ Error fetching user record:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('❌ Error in getUserRecord:', error);
    return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
  }
}

/**
 * Update user role
 */
export async function updateUserRole(userId: string, role: 'talent' | 'scout' | 'admin') {
  try {
    console.log('🔄 Updating user role:', { userId, role });

    const supabase = createClient();
    const { data, error } = await supabase
      .from('profiles')
      .update({
        role: role,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId)
      .select()
      .single();

    if (error) {
      console.error('❌ Error updating user role:', error);
      return { data: null, error };
    }

    console.log('✅ User role updated successfully:', data);
    return { data, error: null };
  } catch (error) {
    console.error('❌ Error in updateUserRole:', error);
    return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
  }
}

/**
 * Get avatar URL with fallback logic
 * 1. If avatar_url exists, use it
 * 2. Else, use default_avatar from stored library
 * 3. Else, fallback to initials or silhouette
 */
export function getAvatarUrl(profile: UserProfile | null, user?: User): string {
  if (!profile && !user) {
    return '/placeholder-avatar.png';
  }

  // First priority: profile avatar_url
  if (profile?.avatar_url) {
    return profile.avatar_url;
  }

  // Second priority: auth user metadata avatar_url
  if (user?.user_metadata?.avatar_url) {
    return user.user_metadata.avatar_url;
  }

  // Third priority: default avatar from library
  if (profile?.default_avatar) {
    return profile.default_avatar;
  }

  // Fallback: placeholder avatar
  return '/placeholder-avatar.png';
}

/**
 * Get user initials for avatar fallback
 */
export function getUserInitials(profile: UserProfile | null, user?: User): string {
  const name = profile?.full_name || 
               profile?.preferred_name || 
               user?.user_metadata?.full_name || 
               user?.email?.split('@')[0] || 
               'U';
  
  return name
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Update user profile
 */
export async function updateUserProfile(userId: string, updates: Partial<UserProfile>) {
  try {
    console.log('🔄 Updating user profile:', { userId, updates });

    const supabase = createClient();
    const { data, error } = await supabase
      .from('profiles')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId)
      .select()
      .single();

    if (error) {
      console.error('❌ Error updating user profile:', error);
      return { data: null, error };
    }

    console.log('✅ User profile updated successfully:', data);
    return { data, error: null };
  } catch (error) {
    console.error('❌ Error in updateUserProfile:', error);
    return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
  }
}

/**
 * Get user profile with role information
 */
export async function getUserProfileWithRole(userId: string) {
  try {
    // Get profile (user data is now in profiles table)
    const supabase = createClient();
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (profileError) {
      console.error('❌ Error fetching profile:', profileError);
      return { data: null, error: profileError };
    }

    // Create user data structure from profile, ensuring role is properly typed
    const userRole = (profile.role as 'talent' | 'scout' | 'admin') || 'talent';
    
    const userData = {
      id: profile.id,
      email: profile.email,
      role: userRole,
      created_at: profile.created_at,
      updated_at: profile.updated_at,
    };

    return {
      data: {
        user: userData,
        profile: profile
      },
      error: null
    };
  } catch (error) {
    console.error('❌ Error in getUserProfileWithRole:', error);
    return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
  }
}

/**
 * Get user profile with explicit field selection including timestamps
 */
export async function getUserProfile(userId: string) {
  try {
    console.log('🔄 Getting user profile for:', userId);
    
    // First try to get all columns with a basic select
    const supabase = createClient();
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('❌ Error fetching profile:', error);
      return { data: null, error };
    }

    console.log('✅ User profile retrieved:', data?.full_name);
    return { data, error: null };
  } catch (error) {
    console.error('❌ Error in getUserProfile:', error);
    return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
  }
}

/**
 * Get all public profiles (for discover page)
 */
export async function getPublicProfiles() {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('public_profile', true)
      .eq('account_approval_status', 'approved')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ Error fetching public profiles:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('❌ Error in getPublicProfiles:', error);
    return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
  }
} 